"use client";

import { useRouter, usePathname, useSearchParams } from "next/navigation";
import React, { useState, useEffect } from "react";

import { useTranslations } from "next-intl";

import { Divider } from "@/components/ui/Divider";
import { cn } from "@/utils/cn";
import { PlansFilterParams } from "@/utils/filterLayouts";
import { replaceInputValue } from "@/utils/replaceInputValue";

import styles from "./style.module.scss";

interface FilterPriceProps {
  currentFilters: PlansFilterParams;
  className?: string;
  currencySymbol?: string;
}

export const FilterPrice: React.FC<FilterPriceProps> = ({
  currencySymbol = "€",
  currentFilters,
  className,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Focus state
  const [focused, setFocus] = useState(false);

  // Local state for inputs to avoid constant URL updates
  const [minPrice, setMinPrice] = useState(
    currentFilters.minPrice?.toString() || "",
  );
  const [maxPrice, setMaxPrice] = useState(
    currentFilters.maxPrice?.toString() || "",
  );

  // Update local state when currentFilters change
  useEffect(() => {
    setMinPrice(currentFilters.minPrice?.toString() || "");
    setMaxPrice(currentFilters.maxPrice?.toString() || "");
  }, [currentFilters.minPrice, currentFilters.maxPrice]);

  const updateFilters = () => {
    const params = new URLSearchParams(searchParams.toString());

    // Remove existing price parameters
    params.delete("minPrice");
    params.delete("maxPrice");

    // Add new price parameters if they have values
    const minVal = parseFloat(minPrice);
    const maxVal = parseFloat(maxPrice);

    if (!isNaN(minVal) && minVal > 0) {
      params.set("minPrice", minVal.toString());
    }

    if (!isNaN(maxVal) && maxVal > 0) {
      params.set("maxPrice", maxVal.toString());
    }

    router.push(`${pathname}?${params.toString()}`);
  };

  const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const replaceValue = replaceInputValue(value);
    setMinPrice(replaceValue);
  };

  const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const replaceValue = replaceInputValue(value);
    setMaxPrice(replaceValue);
  };

  const handleMinPriceBlur = () => {
    updateFilters();
    setFocus(false);
  };

  const handleMaxPriceBlur = () => {
    updateFilters();
    setFocus(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      updateFilters();
    }
  };

  const clearPriceFilter = () => {
    setMinPrice("");
    setMaxPrice("");

    const params = new URLSearchParams(searchParams.toString());
    params.delete("minPrice");
    params.delete("maxPrice");
    router.push(`${pathname}?${params.toString()}`);
  };

  const hasActiveFilter = minPrice !== "" || maxPrice !== "";

  return (
    <div className={cn(styles.filterPrice, className)}>
      <div className={styles.header}>
        <div className={styles.label}>{t("Price")}</div>
        {hasActiveFilter && (
          <button
            className={styles.clearButton}
            onClick={clearPriceFilter}
            title={t("Clear")}
          >
            ×
          </button>
        )}
      </div>

      <div className={cn(styles.inputsWrapper, focused && styles.focussed)}>
        <div className={styles.inputGroup}>
          <input
            type="text"
            value={minPrice}
            onChange={handleMinPriceChange}
            onBlur={handleMinPriceBlur}
            onKeyPress={handleKeyPress}
            placeholder={t("Min")}
            className={styles.priceInput}
            onFocus={() => setFocus(true)}
          />
          <span className={styles.currency}>{currencySymbol}</span>
        </div>

        <Divider
          className={"my-4 h-auto border-gray"}
          variant="vertical"
        />

        <div className={styles.inputGroup}>
          <input
            type="text"
            value={maxPrice}
            onChange={handleMaxPriceChange}
            onBlur={handleMaxPriceBlur}
            onKeyPress={handleKeyPress}
            placeholder={t("Max")}
            className={styles.priceInput}
            onFocus={() => setFocus(true)}
          />
          <span className={styles.currency}>{currencySymbol}</span>
        </div>
      </div>
    </div>
  );
};
